import {DropdownMenu, IconButton, type IconName} from '@wandb/components';
import React, {useContext, useState} from 'react';
import {toast} from 'react-toastify';

import * as Analytics from '../../../services/analytics';
import {PanelBankUpdaterContext} from '../../../state/panelbank/context';
import {useReportCommentAlerts} from '../../../state/reports/hooks';
import {isInIframe} from '../../../util/window';
import {useLoadedReportContext} from '../LoadedReportContext';
import {useReportPageContext} from '../ReportPageContext';
import {ReportMode} from '../types';
import {ReportPageAction} from './ReportPageAction';
import {ReportPageModal} from './ReportPageModal';

// The e2e suite locates menu entries via this attribute
// (tests/integration/playwright/support/reports.ts).
const REPORT_MENU_ITEM_TEST_ATTR = {'data-test': 'report-menu-item'};

type ReportMenuOption = {
  icon: IconName;
  text: string;
  onClick: () => void;
  isEnabled: boolean;
  isToggle?: boolean;
};

type ReportPageMenuActionsProps = {
  viewerOwnsReport: boolean;
};

export const ReportPageMenuActions = ({
  viewerOwnsReport,
}: ReportPageMenuActionsProps) => {
  const {
    isAdmin,
    projectName,
    reportMode,
    viewedReportId,
    viewer,
    isWBFeaturedReport,
    project,
    teamMemberInfo: {viewerIsTeamMember},
    isPublicProject,
  } = useReportPageContext();
  const {reportViewRef, view} = useLoadedReportContext();

  const isInEditMode = reportMode === ReportMode.IS_EDITING;
  const isInViewMode = reportMode === ReportMode.IS_VIEWING;

  const [modalType, setModalType] = useState<ReportPageAction | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const {isSubscribed, subscribe, unsubscribe} = useReportCommentAlerts(
    reportViewRef,
    view
  );
  const {setDisableWaitToLoad} = useContext(PanelBankUpdaterContext);

  // Determine if viewer has ability to edit report
  const hasReportEditAccess =
    viewer != null &&
    !isInIframe() &&
    !project.readOnly &&
    (!view.locked || viewerOwnsReport || isAdmin);
  const hasGalleryEditAccess =
    viewer != null &&
    (viewer.id === view.user.id || isAdmin || viewerOwnsReport);
  const hasEditAccess =
    projectName == null || isWBFeaturedReport
      ? hasGalleryEditAccess
      : hasReportEditAccess;

  // A viewer with an account has access to copy report and turn on on notifications
  const viewerIsLoggedIn = viewer != null && !isInIframe();

  const primaryOptions: ReportMenuOption[] = [
    {
      icon: 'pencil-edit' as const,
      text: isLoading ? 'Loading...' : 'Edit report',
      onClick: () => {
        setIsLoading(true);
        setModalType(ReportPageAction.EditUpdatedReport);
      },
      isEnabled: isInViewMode && hasEditAccess,
    },
    {
      icon: 'photo-picture' as const,
      text: 'Set preview image',
      onClick: () => {
        setModalType(ReportPageAction.SetPreviewImage);
      },
      isEnabled: isInEditMode,
    },
    {
      icon: 'copy' as const,
      text: 'Make a copy',
      onClick: () => {
        setModalType(ReportPageAction.CloneDraft);
      },
      isEnabled: viewerIsLoggedIn,
    },
    {
      icon: 'download' as const,
      text: 'Download',
      onClick: () => {
        setModalType(ReportPageAction.DownloadPDF);
        setDisableWaitToLoad(true);
      },
      isEnabled: true,
    },
    {
      icon: 'bell-notifications' as const,
      text: 'Notifications',
      isToggle: true,
      onClick: () => {
        Analytics.reportNotificationsToggled({
          isPublicProject,
          reportId: viewedReportId,
          actionTriggered: isSubscribed ? 'unsubscribe' : 'subscribe',
        });

        if (isSubscribed) {
          unsubscribe();
          toast('Notifications successfully turned off.');
        } else {
          subscribe();
          toast('Notifications successfully turned on.');
        }
      },
      isEnabled: (isWBFeaturedReport || isInViewMode) && viewerIsLoggedIn,
    },
    {
      icon: 'star' as const,
      text: 'View stars',
      onClick: () => {
        Analytics.reportViewLikesClicked({isPublicProject});
        setModalType(ReportPageAction.ViewStars);
      },
      isEnabled:
        isInViewMode &&
        (isAdmin || (!isWBFeaturedReport && viewerIsTeamMember)),
    },
  ].filter(opt => opt.isEnabled);

  const secondaryOptions: ReportMenuOption[] = [
    {
      icon: 'folder-project-move' as const,
      text: 'Move report',
      onClick: () => {
        setModalType(ReportPageAction.MoveReport);
      },
      isEnabled: isInViewMode && isAdmin, // This is a W&B admin or server admin specific ability
    },
    {
      icon: 'delete' as const,
      text: 'Delete draft',
      onClick: () => {
        setModalType(ReportPageAction.DeleteDraft);
      },
      isEnabled: !isWBFeaturedReport && isInEditMode,
    },
  ].filter(opt => opt.isEnabled);

  if (primaryOptions.length + secondaryOptions.length === 1) {
    return (
      <>
        <IconButton
          data-test="single-report-overflow-action-button"
          aria-label="More actions"
          icon="pencil-edit"
          variant={isInEditMode ? 'secondary' : 'ghost'}
          onPress={primaryOptions[0].onClick}
        />
        <ReportPageModal
          modalType={modalType}
          setModalType={setModalType}
          setIsLoading={setIsLoading}
        />
      </>
    );
  }

  return (
    <div>
      <DropdownMenu.Trigger>
        <IconButton
          aria-label="More actions"
          data-test="report-overflow-menu-button"
          icon="overflow-horizontal"
          tooltip={{children: 'More actions'}}
          variant={isInEditMode ? 'secondary' : 'ghost'}
        />
        <DropdownMenu.Content placement="bottom end">
          {primaryOptions.map(option => (
            <DropdownMenu.Item
              key={option.text}
              {...REPORT_MENU_ITEM_TEST_ATTR}
              onAction={() => option.onClick()}>
              <DropdownMenu.ItemDecorationLeft
                iconProps={{icon: option.icon}}
              />
              <DropdownMenu.ItemLabel>{option.text}</DropdownMenu.ItemLabel>
              {option.isToggle && (
                <DropdownMenu.ItemDecorationRight
                  pillProps={{textValue: isSubscribed ? 'On' : 'Off'}}
                />
              )}
            </DropdownMenu.Item>
          ))}
          {secondaryOptions.length > 0 && <DropdownMenu.Separator />}
          {secondaryOptions.map(option => (
            <DropdownMenu.Item
              key={option.text}
              {...REPORT_MENU_ITEM_TEST_ATTR}
              onAction={() => option.onClick()}>
              <DropdownMenu.ItemDecorationLeft
                iconProps={{icon: option.icon}}
              />
              <DropdownMenu.ItemLabel>{option.text}</DropdownMenu.ItemLabel>
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Trigger>
      <ReportPageModal
        modalType={modalType}
        setModalType={setModalType}
        setIsLoading={setIsLoading}
      />
    </div>
  );
};
